import React, { useCallback, useMemo, useRef, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { Link, Text, Popover, Icon, ListItem, Button } from "@bookingcom/bui-react";
import Frame from "../Frame";

import {
  ShareIcon,
  CopyIcon,
  EmailIcon,
  CheckmarkSelectedIcon
} from "@bookingcom/bui-assets-react/streamline/index.js";
import useGlobalContext from "../../../hooks/useGlobalContext";
import { getModalTitle } from "../FlightDetailsInner/components/flightDetailsUtils";
import { ACCOMMODATIONS_DOMAIN_URL, ACCOMMODATIONS_DOMAIN_URL_DQS } from "../../../constants";
// eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
import { useStore } from "../../../store";
import { useI18n, t } from "@bookingcom/lingojs-react";
import useEvents from "../../../hooks/useEvents";
import { isOfMetaOrigin } from "../../../utils/marketing-url-params";
import { UIFlightData, UIShareUrlResponse } from "@flights/types";
import { clientFetch } from "@flights/client-fetch";
import useContextName from "../../../hooks/useContextName";
import { BOOKING_DEFAULT_AFFILIATE_ID, FLIGHTS_SHARE_DIRECT_LABEL, FLIGHTS_SHARE_META_LABEL } from "@flights/constants";
import { isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import useProfiler from "../../../hooks/useProfiler";

type ShareFlightsProps = {
  flight?: UIFlightData;
};

const ShareFlight = (props: ShareFlightsProps) => {
  const [sharePopoverActive, setSharePopoverActive] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [fetchedUrl, setFetchedUrl] = useState("");

  const i18n = useI18n();
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const { search } = useStore();
  const { flight } = props;
  const contextName = useContextName();

  const { env, userAgent } = useGlobalContext();
  // eslint-disable-next-line no-restricted-syntax
  const shareButtonRef = useRef(null);
  const { jsError } = useEvents();
  const { isLoggedIn } = useProfiler();

  const isMeta = isOfMetaOrigin();

  const copyToClipboard = useCallback(
    (url: string) => {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          setLinkCopied(true);
        })
        .catch((err) => {
          jsError(err);
        });
    },
    [jsError]
  );
  const getShareUrl = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);

    for (const param of searchParams.keys()) {
      if (param.startsWith("ext-")) {
        searchParams.delete(param);
      }
    }

    if (!isFeatureRunningClientSide("FLIGHTS_PRICING_POPULATE_AFFILIATE_ID_FOR_LINK_SHARING")) {
      searchParams.set("aid", BOOKING_DEFAULT_AFFILIATE_ID);
    }
    searchParams.set("label", isMeta ? FLIGHTS_SHARE_META_LABEL : FLIGHTS_SHARE_DIRECT_LABEL);

    return new URL(`${location.pathname}?${searchParams.toString()}`, location.origin).toString();
  }, [isMeta]);

  const nativeShareTitle = useMemo(() => {
    return flight?.segments
      .map(({ departureAirport, arrivalAirport }) => `${departureAirport.cityName} – ${arrivalAirport.cityName}`)
      .join(" | ");
  }, [flight?.segments]);

  const shareData = useMemo(
    () => ({
      title: nativeShareTitle,
      text: getModalTitle(i18n, flight?.segments, search.type),
      url: ""
    }),
    [flight?.segments, i18n, nativeShareTitle, search.type]
  );

  const onShare = useCallback(async () => {
    if (!fetchedUrl) {
      if (env !== "prod") {
        setFetchedUrl(getShareUrl());
        shareData.url = getShareUrl();
      } else {
        const response = await clientFetch<UIShareUrlResponse>("/api/urlSharing", {
          method: "post",
          body: JSON.stringify({
            url: getShareUrl()
          }),
          headers: {
            "Content-Type": "application/json",
            "X-Flights-Context-Name": contextName
          }
        });

        const shortUrl = response?.id
          ? `${env === "prod" ? ACCOMMODATIONS_DOMAIN_URL : ACCOMMODATIONS_DOMAIN_URL_DQS}/${response.id}`
          : getShareUrl();
        setFetchedUrl(shortUrl);
        shareData.url = shortUrl;
      }
    } else {
      shareData.url = fetchedUrl;
    }

    if (userAgent.isMobile && window.navigator?.canShare?.(shareData)) {
      window.navigator
        .share(shareData)
        .then()
        .catch((err) => {
          jsError(err);
        });
    } else {
      setSharePopoverActive(true);
    }
  }, [contextName, env, fetchedUrl, getShareUrl, jsError, shareData, userAgent.isMobile]);

  const openMail = useCallback(
    (url: string) => {
      window.open(`mailto:?subject=${i18n.trans(t("flights_meta_fd_share_msg"))}&body=${encodeURIComponent(url)}`);
    },
    [i18n]
  );

  const handleMailClick = useCallback(async () => {
    openMail(fetchedUrl);
  }, [fetchedUrl, openMail]);

  const handleCopyClick = useCallback(async () => {
    copyToClipboard(fetchedUrl);
  }, [copyToClipboard, fetchedUrl]);

  return (
    <Frame direction="row">
      {isLoggedIn() ? (
        <Button.Aligner alignment="start">
          <Button
            size="large"
            onClick={onShare}
            variant="tertiary"
            icon={ShareIcon}
            text={i18n.trans(t("th_desktop_trip_list_flights_share"))}
            attributes={{
              ref: shareButtonRef
            }}
          />
        </Button.Aligner>
      ) : (
        <Link
          text={i18n.trans(t("flights_meta_fd_share"))}
          attributes={{
            ref: shareButtonRef
          }}
          onClick={onShare}
          icon={ShareIcon}
        />
      )}
      <Popover
        triggerRef={shareButtonRef}
        triggerType="click"
        closeAriaLabel={i18n.trans(t("close"))}
        active={sharePopoverActive}
        onClose={() => {
          setLinkCopied(false);
          setSharePopoverActive(false);
        }}
      >
        <Popover.Content title={i18n.trans(t("flights_meta_fd_share_modal_header"))}>
          <Frame direction="column">
            <Frame pb={2}>
              <div aria-live="polite">
                {linkCopied ? (
                  <ListItem
                    startSlot={<Icon svg={CheckmarkSelectedIcon} size="small" color="constructive" />}
                    edgeSpacing
                  >
                    <Text tagName="span" color="constructive" variant="body_2">
                      {i18n.trans(t("flights_meta_fd_share_modal_copied"))}
                    </Text>
                  </ListItem>
                ) : (
                  <CopyToClipboard text={fetchedUrl}>
                    <ListItem startSlot={<Icon size="small" svg={CopyIcon} />} onClick={handleCopyClick}>
                      {i18n.trans(t("flights_meta_fd_share_modal_copy"))}
                    </ListItem>
                  </CopyToClipboard>
                )}
              </div>
            </Frame>
            <Frame pb={2}>
              <ListItem
                startSlot={<Icon size="small" svg={EmailIcon} />}
                attributes={{ target: "_blank" }}
                onClick={handleMailClick}
              >
                {i18n.trans(t("flights_meta_fd_share_modal_email"))}
              </ListItem>
            </Frame>
          </Frame>
        </Popover.Content>
      </Popover>
    </Frame>
  );
};

export default ShareFlight;
